import { apiPatch, apiPost } from "./api";
import { applyToken } from "./auth";
import type {
  AuthExchangeCodeRequestBody,
  AuthExchangeCodeResponse,
  AuthRefreshRequestBody,
  AuthRefreshResponse,
  WhoamiResponse,
} from "../../shared-domain/auth-service/contract";

/**
 * authCode を token に交換し、sessionStorage に保存
 * @param code 認証コード
 * @param apiEndpoint API エンドポイント
 * @param tokenStoreName トークンストア名
 * @returns AuthExchangeCodeResponse
 */
export async function callAuthExchange(
  code: string,
  apiEndpoint: string,
  tokenStoreName: string,
): Promise<AuthExchangeCodeResponse> {
  const url = `${apiEndpoint}/auth/exchange-code`;
  const response = await apiPost<
    AuthExchangeCodeResponse,
    AuthExchangeCodeRequestBody
  >(url, { code });

  if (response.success) {
    const { idToken, accessToken, refreshToken } = response.body;
    applyToken({
      tokenStoreName,
      accessToken,
      idToken,
      refreshToken,
    });
  }

  return response;
}

/**
 * whoami API を呼び出してユーザー情報を取得
 * @param apiEndpoint API エンドポイント
 * @returns WhoamiResponse
 */
export async function callWhoami(apiEndpoint: string): Promise<WhoamiResponse> {
  const url = `${apiEndpoint}/auth/whoami`;
  return apiPost<WhoamiResponse>(url, {});
}

/**
 * callPatchCurrentTenant API を呼び出してユーザ情報を更新
 * @param apiEndpoint API エンドポイント
 * @param currentTenantId 現在のテナントID
 * @returns void
 */
export async function callPatchCurrentTenant(
  apiEndpoint: string,
  currentTenantId: string,
) {
  const url = `${apiEndpoint}/auth/me`;
  await apiPatch(url, { currentTenantId, tenantId: currentTenantId });
}

/**
 * refreshToken を使用してトークンをリフレッシュ
 * @param refreshToken リフレッシュトークン
 * @param authRefreshUrl リフレッシュAPIのURL
 * @returns { access, id, refresh } 新しいトークン
 */
export async function callAuthRefresh(
  refreshToken: string,
  authRefreshUrl: string,
): Promise<{ access: string; id: string; refresh?: string }> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch(authRefreshUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken } as AuthRefreshRequestBody),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      // PRODUCT_DEV-797: 呼び出し側（TokenProvider）が「401=トークンが死んでいる」と
      // 「ネットワーク/サーバ側の一時障害」を区別できるよう status を載せる。
      // メッセージ文字列から拾わせない。作法は handleApiError（utils/api.ts / PD-212）に揃える。
      const error = new Error(
        `Token refresh failed: ${response.status} ${response.statusText} - ${errorText}`,
      ) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    const data = (await response.json()) as AuthRefreshResponse;

    // Validate response structure
    if (!data.body?.accessToken || !data.body?.idToken) {
      throw new Error("Invalid refresh response: missing required tokens");
    }

    return {
      access: data.body.accessToken,
      id: data.body.idToken,
      refresh: data.body.refreshToken,
    };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Token refresh timeout", { cause: error });
    }
    throw error;
  }
}
